import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { View, Heading, colors, Icon } from '@fairygodboss/ui';
import { useEventRegistration } from '../EventRegistrationContext';
import { GridColumnWrapper } from './GridColumnWrapper';
import './froala_style.scss';
import { buildImagePath } from '../../../JobComponents/Company/Image/logo';
import { getFormattedDateString, getFormattedTimeString } from './helpers';
import { SocialShareCta } from './SocialShareCta';

const SponsorDesktop = () => {
    const { sponsors, event } = useEventRegistration();
    const { label } = event;
    return (
        <View pt="28px">
            <Heading variant="headingXs" fontWeight="700" mb="16px">
                {label}
            </Heading>
            {sponsors.map((s) => (
                <View
                    mb="12px"
                    display="flex"
                    color={colors.neutral[100]}
                    alignItems={{
                        _: 'flex-start',
                        xs: 'center',
                        sm: 'center',
                        md: 'flex-start',
                    }}
                    key={s?.name?.split(' ')?.join('-')}
                    width="100%"
                >
                    <SponsorImage image={s.image} mr="12px" />
                    {s.name}
                </View>
            ))}
        </View>
    );
};

const Sidebar = forwardRef((props, ref) => {
    const { event } = useEventRegistration();
    const { eventType, startTime, endTime, sponsors, label, image } = event;

    const src = buildImagePath(image?.src, 'img', {
        img: 'c_scale,f_auto,q_auto,fl_lossy',
    });

    const startDateString = getFormattedDateString(startTime);
    const startTimeString = getFormattedTimeString(startTime);
    const endTimeString = getFormattedTimeString(endTime);

    return (
        <View
            pl="24px"
            ref={ref}
            display={{ _: 'none', xs: 'none', sm: 'none', md: 'inherit' }}
        >
            <View as="img" src={src} width="100%" alt={image?.alt} />
            <View
                mt="16px"
                display="flex"
                flexDirection="column"
                borderTop={`2px solid ${colors.info.base}`}
            >
                <Heading
                    pt="16px"
                    variant="headingXs"
                    color={colors.primary[60]}
                    lineHeight="20px"
                >
                    {eventType?.toUpperCase()}
                </Heading>
                <View display="flex" alignItems="center" mt="18px">
                    <View mr="12px" height="20px">
                        <Icon variant="calendar_24" />
                    </View>
                    {startDateString}
                </View>
                <View display="flex" mt="12px" alignItems="center">
                    <View mr="12px" height="20px">
                        <Icon variant="clock_24" mr="12px" />
                    </View>
                    {`${startTimeString} - ${endTimeString} ET`}
                </View>
                <SponsorDesktop sponsors={sponsors} label={label} />
                <SocialShareCta showLabel />
            </View>
        </View>
    );
});

const SponsorImage = ({ image, ...others }) => {
    const src = buildImagePath(image?.src, 'img', {
        img: 'c_scale,f_auto,q_auto,fl_lossy,w_48',
    });
    return (
        <View
            borderRadius="4px"
            display="flex"
            alignItems="center"
            mr="12px"
            mb={{
                _: '12px',
                xs: '0px',
                sm: '0px',
                md: '12px',
            }}
            boxShadow="0px 4px 8px rgba(53, 53, 53, 0.2)"
            height="48px"
            width="48px"
            {...others}
        >
            <View
                as="img"
                style={{
                    width: '48px',
                    aspectRatio: '1/0',
                }}
                src={src}
                alt={image.alt}
            />
        </View>
    );
};

const EventBodyFooter = ({ eventType }) => {
    const innerText =
        eventType === 'Webinar'
            ? null
            : 'Important note: Fairygodboss is an inclusive community and when we use the term "women," we refer to cis women, trans women and nonbinary individuals.';
    return (
        <View
            fontStyle="italic"
            color={colors.neutral[100]}
            fontSize={{ _: '16px', md: '18px' }}
            lineHeight={{ _: '26px', md: '30px' }}
            borderTop={`1px solid ${colors.secondary[20]}`}
            pt={{ _: '24px', md: '32px' }}
            mt={{ _: '24px', md: '32px' }}
        >
            {innerText || (
                <>
                    <br />
                    <br />
                </>
            )}
        </View>
    );
};

export const EventBody = () => {
    const { event, videoRef, playVideo, sponsors } = useEventRegistration();
    const [minHeight, setMinHeight] = useState(500);
    const sidebarRef = useRef();
    const { description, video, eventType } = event;

    const handleResize = useCallback(() => {
        const height = sidebarRef?.current?.offsetHeight + 80;
        setMinHeight(height);
    }, [sidebarRef]);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    return (
        <GridColumnWrapper
            backgroundColor={colors.neutral[0]}
            gridAlignment="center"
            minHeight={{
                _: 'inherit',
                md: minHeight,
            }}
            gridColumnSpacing={{ xs: 12, sm: 9, md: 12, lg: 10 }}
            pt={{ _: '32px', md: '40px' }}
            px={{ _: '16px', sm: '0px', md: '24px', lg: '0px' }}
            pb={{ _: '32px', md: '48px' }}
        >
            <View position="relative">
                {sponsors.length ? (
                    <View
                        css={{ gap: '24px' }}
                        mb="24px"
                        display={{ _: 'flex', md: 'none' }}
                        justifyContent="space-between"
                        flexWrap="wrap"
                    >
                        <SponsorDesktop />
                        <View width={{ _: '100%', sm: 'inherit' }}>
                            <SocialShareCta />
                        </View>
                    </View>
                ) : null}
                <GridColumnWrapper
                    gridColumnSpacing={{ _: 12, md: 3, lg: 3.6 }}
                    position="absolute"
                    top="0px"
                    gridAlignment="flex-end"
                    display={{
                        _: 'none',
                        xs: 'none',
                        sm: 'none',
                        md: 'inherit',
                    }}
                >
                    <Sidebar ref={sidebarRef} />
                </GridColumnWrapper>
                <GridColumnWrapper
                    gridColumnSpacing={{ _: 12, md: 8, lg: 8.4 }}
                >
                    <Heading
                        variant="headingLg"
                        display={{ _: 'none', md: 'inherit' }}
                        pb="16px"
                        as="h2"
                    >
                        Event details
                    </Heading>
                    <View className="fr-view">
                        <View
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </View>
                    {video ? (
                        <View
                            borderTop={`1px solid ${colors.secondary[20]}`}
                            pt={{ _: '24px', md: '32px' }}
                            mt={{ _: '24px', md: '32px' }}
                        >
                            <View
                                position="relative"
                                paddingTop="56.25%"
                                ref={videoRef}
                            >
                                <iframe
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    src={`${video?.src}${
                                        playVideo ? '?autoplay=1' : ''
                                    }`}
                                    alt={video.alt}
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                    title="video"
                                />
                            </View>
                        </View>
                    ) : (
                        <EventBodyFooter eventType={eventType} />
                    )}
                </GridColumnWrapper>
            </View>
        </GridColumnWrapper>
    );
};

EventBody.displayName = 'EventBody';
